import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { color, radius } from "../../styles/theme";

/**
 * Compares two semver strings (e.g. "1.0.6" vs "1.0.7").
 * Returns true if `installed` is strictly older than `latest`.
 */
export function isPSSOutdated(installed, latest) {
  if (!installed || !latest) return false;
  const parse = (v) => String(v).split(".").map((n) => parseInt(n, 10) || 0);
  const a = parse(installed);
  const b = parse(latest);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av < bv) return true;
    if (av > bv) return false;
  }
  return false;
}

/**
 * Shows a prominent warning banner when the installed PSS version is outdated.
 * Renders nothing when versions are in sync or when PSS is not running.
 */
const PSSUpdateBanner = ({ installedVersion, latestVersion, style }) => {
  const { t } = useTranslation("common");
  if (!isPSSOutdated(installedVersion, latestVersion)) return null;

  return (
    <div style={{ ...s.banner, ...style }}>
      <span style={s.iconBadge}><AlertTriangle size={18} strokeWidth={2} /></span>
      <div style={s.body}>
        <strong style={s.title}>{t("pssUpdateBanner.title")}</strong>
        <span style={s.msg}>
          {t("pssUpdateBanner.messagePrefix")} <strong>v{installedVersion}</strong> {t("pssUpdateBanner.messageMiddle")}{" "}
          <strong>v{latestVersion}</strong>. {t("pssUpdateBanner.messageSuffix")}
        </span>
      </div>
      <Link to="/companion" className="btn btn-sm" style={s.btn}>{t("pssUpdateBanner.updateCta")}</Link>
    </div>
  );
};

const s = {
  banner: {
    display: "flex", alignItems: "center", gap: 14, background: color.warning50,
    border: "1px solid #fde68a", borderRadius: radius.md, padding: "14px 18px", marginBottom: 20, flexWrap: "wrap",
  },
  iconBadge: {
    width: 34, height: 34, borderRadius: radius.sm, background: "#fef3c7", color: "#b45309",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  body: { flex: 1, display: "flex", flexDirection: "column", gap: 3, minWidth: 200 },
  title: { fontSize: 13.5, fontWeight: 700, color: "#92400e" },
  msg: { fontSize: 12.5, color: "#78350f", lineHeight: 1.55 },
  btn: { flexShrink: 0, background: "#b45309", color: "#fff", textDecoration: "none" },
};

export default PSSUpdateBanner;
